<template>
  <div ref="wrapperRef" class="m-approval-wrapper">
    <div class="m-approval-container">
      <!-- Approval groups/steps -->
      <div
        v-for="(group, index) in approvalGroups"
        :key="group.id || index"
        class="approval-group"
        :class="getGroupStatusClass(group)"
      >
        <div class="approval-row">
          <!-- Step / title -->
          <div class="approval-step-cell">
            <div class="approval-step-label">Этап {{ index + 1 }}</div>
            <div class="approval-step-name">
              {{ group.name || `Согласование ${index + 1}` }}
            </div>
          </div>

          <!-- Participant -->
          <div class="approval-participant-cell">
            <span class="cell-label">Участник:</span>
            <div class="cell-value with-icons">
              <i class="pi pi-user"></i>
              <span :title="group.signer">{{ group.signer || '—' }}</span>
            </div>
          </div>

          <div class="approval-status-cell">
            <div class="cell-value cell-status">
              <i
                v-if="group?.status"
                :class="getStatusIcon(group.status)"
              />
              <!-- <span :style="{ color: getStatusColor(group.status) }">
                {{ getStatusText(group.status || '') }}
              </span> -->
              <i
                v-if="group?.userStatus"
                :class="getUserStatus(group.userStatus)"
              />
            </div>
          </div>

          <!-- Comment -->
          <div class="approval-comment-cell">
            <span class="cell-label">Комментарий:</span>
            <div class="cell-value with-icons">
              <i class="pi pi-comment"></i>
              <span>{{ group.comment || '—' }}</span>
            </div>
          </div>

          <!-- Start / End dates -->
          <div class="approval-date-cell">
            <span class="cell-label">Начало</span>
            <div class="cell-value">
              {{ group.startDate ? formatDate(group.startDate) : '—' }}
            </div>
          </div>
          <div class="approval-date-cell">
            <span class="cell-label">Окончание</span>
            <div class="cell-value">
              {{
                group.signDate ? formatDate(group.signDate) : '—'
              }}
            </div>
          </div>
        </div>

        <div v-if="editable && !readonly && group.status === 'pending'" class="approval-actions">
          <Button
            icon="pi pi-check"
            label="Согласовать"
            class="p-button-sm p-button-success"
            @click="handleApprove(group, index)"
          />
          <Button
            icon="pi pi-times"
            label="Отклонить"
            class="p-button-sm p-button-danger"
            @click="handleReject(group, index)"
          />
          <Button
            icon="pi pi-pencil"
            label="Комментарий"
            class="p-button-sm p-button-text"
            @click="handleEditComment(group, index)"
          />
        </div>

        <div v-if="!!group?.startDate" class="approval-stage-start">
          Начало этапа: {{ formatDate(group.startDate) }}
        </div>
      </div>

      <!-- Add approval step (if editable) -->
      <div v-if="editable && !readonly && canAddSteps" class="approval-add">
        <Button
          icon="pi pi-plus"
          label="Добавить согласующего"
          class="p-button-outlined"
          @click="handleAddStep"
        />
      </div>

      <!-- Empty state -->
      <div v-if="approvalGroups.length === 0" class="approval-empty">
        <i class="pi pi-file-check"></i>
        <p>Нет этапов согласования</p>
      </div>
    </div>

    <!-- Comment input (if nested approval) -->
    <div v-if="isNested" class="approval-comment-input">
      <Textarea
        v-model="commentText"
        rows="3"
        placeholder="Комментарий к согласованию"
        class="w-full"
      />
    </div>

    <div v-if="hint" class="m-approval-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface ApprovalGroup {
  id?: string | number
  name?: string
  signer?: string
  signDate?: Date | string
  startDate?: Date | string
  status?: 'pending' | 'approved' | 'rejected' | 'completed'
  comment?: string
}

interface Props {
  modelValue?: ApprovalGroup[]
  editable?: boolean
  readonly?: boolean
  isNested?: boolean
  canAddSteps?: boolean
  hint?: string
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  editable: true,
  readonly: false,
  isNested: false,
  canAddSteps: true,
  hint: '',
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [groups: ApprovalGroup[]]
  'change': [groups: ApprovalGroup[]]
  'approve': [group: ApprovalGroup]
  'reject': [group: ApprovalGroup]
  'edit-comment': [group: ApprovalGroup]
  'add-step': []
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const commentText = ref('')
const approvalGroups = computed(() => props.modelValue || [])

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const getGroupStatusClass = (group: ApprovalGroup) => ({
  'group-approved': group.status === 'approved' || group.status === 'completed',
  'group-rejected': group.status === 'rejected',
  'group-pending': group.status === 'pending'
})

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'approved':
    case 'completed':
      return 'pi pi-check-circle'
    case 'rejected':
      return 'pi pi-times-circle'
    case 'pending':
      return 'pi pi-clock'
    default:
      return ''
  }
}

const getUserStatus = (status: string) => {
  switch (status) {
    case 'userEdit':
      return 'pi pi-file-edit'
    case 'userSign':
      return 'pi pi-pencil'
    case 'userApprove':
      return 'pi pi-thumbs-up'
    default:
      return ''
  }
}

// const getStatusText = (status: string) => {
//   switch (status) {
//     case 'approved':
//       return 'Согласовано'
//     case 'completed':
//       return 'Завершено'
//     case 'rejected':
//       return 'Отклонено'
//     case 'pending':
//       return 'Ожидает'
//     default:
//       return status
//   }
// }

const formatDate = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleString('ru-RU')
}

const handleApprove = (group: ApprovalGroup, index: number) => {
  const updatedGroups = [...approvalGroups.value]
  updatedGroups[index] = {
    ...group,
    status: 'approved',
    signDate: new Date(),
    comment: commentText.value || group.comment
  }
  emit('update:modelValue', updatedGroups)
  emit('change', updatedGroups)
  emit('approve', group)
  commentText.value = ''
}

const handleReject = (group: ApprovalGroup, index: number) => {
  const updatedGroups = [...approvalGroups.value]
  updatedGroups[index] = {
    ...group,
    status: 'rejected',
    signDate: new Date(),
    comment: commentText.value || group.comment
  }
  emit('update:modelValue', updatedGroups)
  emit('change', updatedGroups)
  emit('reject', group)
  commentText.value = ''
}

const handleEditComment = (group: ApprovalGroup, index: number) => {
  emit('edit-comment', group)
}

const handleAddStep = () => {
  emit('add-step')
}
</script>

<style scoped lang="scss">
.m-approval-wrapper {
  width: 100%;
}

.m-approval-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.approval-group {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #94a3b8;
  border-radius: 4px;
  padding: 1rem;
  transition: all 0.2s;

  &.group-approved {
    border-left-color: #10b981;
    background: #f0fdf4;
  }

  &.group-rejected {
    border-left-color: #ef4444;
    background: #fef2f2;
  }

  &.group-pending {
    border-left-color: #f59e0b;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.approval-row {
  display: grid;
  grid-template-columns: 120px 1.4fr 1fr 1.6fr 140px 140px;
  column-gap: 0.5rem;
  align-items: flex-start;
  font-size: 0.8125rem;
}

.approval-step-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.approval-step-label {
  font-weight: 600;
  color: #4b5563;
}

.approval-step-name {
  font-size: 0.8125rem;
  color: #4b5563;
}

.cell-label {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.125rem;
}

.cell-value {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #111827;
}

.cell-value.with-icons i {
  font-size: 0.75rem;
}

.icon-action {
  color: #6b7280;
  cursor: pointer;
}

.icon-action:hover {
  color: #111827;
}

.approval-participant-cell,
.approval-status-cell,
.approval-comment-cell,
.approval-date-cell {
  padding: 0.25rem 0.25rem 0;
}
.approval-status-cell{
  height: 100%;
}

.cell-status {
  font-weight: 600;
  gap: 1rem;
  height: 100%;
  .pi-check-circle{
    color: #10b981
  }
  .pi-times-circle{
    color: #ef4444;
  }
  .pi-clock{
    color: #f59e0b;
  }
}

.approval-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.approval-add {
  padding: 1rem;
  border: 2px dashed #d1d5db;
  border-radius: 4px;
  display: flex;
  justify-content: center;

  &:hover {
    border-color: var(--primary-color, #1e3a6e);
    background: color-mix(in srgb, var(--primary-color, #1e3a6e) 6%, white);
  }
}

.approval-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
  }
}

.approval-comment-input {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.m-approval-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

.approval-stage-start {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #4b5563;
}
</style>

